import { ArrowLeft, Loader2, ShieldAlert } from "lucide-react";
import { useAdminConversation } from "../api/aronAdminApi";
import { AronMessageList } from "./AronMessageList";
import type { AronProvider } from "../types";

/**
 * Wspólne części podglądu CUDZEJ rozmowy z ARON-em (audyt CEO+DEV, 11E/C).
 *
 * Jeden renderer dla dwóch miejsc: strony „Audyt ARON" i karty podglądu
 * otwieranej wprost w ARON-ie (zakładka „Wszystkie" na liście rozmów). Gdyby
 * transkrypt rysowały dwa osobne komponenty, poprawka w jednym cicho omijałaby
 * drugi — a to jest widok, w którym widać płace, finanse i dane osobowe.
 *
 * Każde wejście tutaj pobiera transkrypt endpointem admina, który sam zapisuje
 * ślad RODO (AronAdminAccessLog) — dlatego podgląd NIGDY nie idzie zwykłymi
 * endpointami właściciela wątku (te i tak zwracają 404).
 */

export function fmtInt(n: number): string {
  return (n || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

export function fmtUsd(value: string | number): string {
  const n = typeof value === "string" ? parseFloat(value) : value;
  if (!n) return "0,00";
  const decimals = Math.abs(n) >= 1 ? 2 : 4;
  if (Math.abs(n) > 0 && Math.abs(n) < 0.0001) return "<0,0001";
  const [intPart, decPart] = n.toFixed(decimals).split(".");
  return `${intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ")},${decPart}`;
}

export function fmtDateTime(s: string | null): string {
  if (!s) return "—";
  return new Date(s).toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export const PROVIDER_DOT: Record<AronProvider, string> = {
  claude: "bg-sky-500",
  deepseek: "bg-violet-500",
};

export function shortModel(id: string): string {
  if (id.toLowerCase().startsWith("deepseek")) return "DeepSeek";
  return id.replace(/^claude-/, "").replace(/-/g, ".");
}

export function ProviderChip({
  provider,
  model,
}: {
  provider: AronProvider;
  model: string;
}) {
  return (
    <span className="inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600 dark:bg-gray-700 dark:text-gray-300">
      <span className={`h-1.5 w-1.5 rounded-full ${PROVIDER_DOT[provider]}`} />
      {shortModel(model)}
    </span>
  );
}

/**
 * Pełny transkrypt jednej cudzej rozmowy — nagłówek (czyja, na czym, za ile)
 * i wiadomości. Bez komponowania odpowiedzi: to podgląd, nie czat.
 */
export function AronAuditTranscript({
  id,
  onBack,
  /** Pasek „to cudza rozmowa" — na karcie w ARON-ie tak, na stronie audytu
   * ostrzeżenie stoi nad całą listą. */
  showNotice = false,
}: {
  id: string;
  onBack?: () => void;
  showNotice?: boolean;
}) {
  const { data, isLoading, isError } = useAdminConversation(id);
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-gray-500">
        <Loader2 size={16} className="animate-spin" /> Wczytuję transkrypt…
      </div>
    );
  }
  // Bez tego karta podglądu, której serwer odmówił (np. po zdjęciu uprawnienia
  // CEO+DEV albo po usunięciu wątku), kręciła spinner bez końca — a to jest
  // dokładnie ten stan, w którym trzeba powiedzieć wprost, że dostępu NIE MA.
  if (isError || !data) {
    return (
      <div className="flex items-start gap-2 p-6 text-sm text-gray-500">
        <ShieldAlert size={15} className="mt-0.5 shrink-0 text-amber-500" />
        Nie udało się wczytać tej rozmowy — brak uprawnień albo wątek już nie
        istnieje.
      </div>
    );
  }
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-start justify-between gap-2 border-b border-gray-200 p-3 dark:border-gray-700">
        <div className="min-w-0">
          {onBack && (
            <button
              onClick={onBack}
              className="mb-1 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 lg:hidden dark:hover:text-gray-200"
            >
              <ArrowLeft size={13} /> Lista
            </button>
          )}
          <div className="truncate font-medium text-gray-900 dark:text-gray-100">
            {data.title || "(bez tytułu)"}
            {data.deleted_at && (
              <span className="ml-1 rounded bg-gray-200 px-1 text-[10px] text-gray-500 dark:bg-gray-700">
                usunięta
              </span>
            )}
          </div>
          <div className="text-xs text-gray-400">
            {data.user_name} · {data.user_email}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-sm font-semibold text-red-600">
            {fmtUsd(data.total_cost_usd)} $
          </div>
          <div className="mt-0.5">
            <ProviderChip provider={data.provider} model={data.model_id} />
          </div>
        </div>
      </div>
      {showNotice && (
        <div className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300">
          <ShieldAlert size={13} className="mt-0.5 shrink-0" />
          Podgląd cudzej rozmowy — tylko do odczytu. Wejście jest zapisywane w
          rejestrze dostępów (RODO).
        </div>
      )}
      <div data-aron-scroll className="min-h-0 flex-1 overflow-y-auto">
        <AronMessageList messages={data.messages} conversationId={data.id} readOnly />
      </div>
    </div>
  );
}
